import { useState } from "react";
import { generateSlug } from "@/lib/utils";
import { ChevronDown, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cities } from "@/data/cities";
import { servicesData, industriesData } from "@/pages/Index";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  
  return (
    <>
      {showBanner && (
        <div className="bg-primary text-accent text-center text-xs font-bold py-2 px-4 uppercase tracking-wider relative z-[60]">
          <span className="inline-block animate-pulse mr-2">🔥</span> 
          Limited Time: Get a Free Custom Growth Blueprint (Only 3 spots left this week) — 
          <a href="/#contact" className="underline hover:text-primary-foreground ml-1">Claim Yours Now</a>
          <button onClick={() => setShowBanner(false)} className="absolute right-4 top-1/2 -translate-y-1/2 text-accent hover:text-primary-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    <header className="fixed left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-primary/10 transition-all duration-300" style={{ top: showBanner ? '32px' : '0' }}>
      <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 font-bold text-lg tracking-tight">
          <span className="w-9 h-9 bg-primary flex items-center justify-center text-accent shadow-md border border-accent/30">D</span>
          <span>DANU MARKETING</span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
          <a href="/about" className="hover:text-primary transition-colors">About Us</a>
          
          <div className="relative group">
            <button className="hover:text-primary transition-colors flex items-center gap-1 py-4 -my-4">
              Services <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="w-[600px] bg-background border border-primary/30 shadow-2xl p-6 grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto mt-4">
                <div className="col-span-2 mb-2 pb-2 border-b border-primary/10">
                  <p className="text-xs font-bold text-accent uppercase tracking-wider">Our Services</p>
                </div>
                {servicesData.map(service => (
                  <a 
                    key={service.title} 
                    href={`/services/${generateSlug(service.title)}`} 
                    className="text-sm hover:text-accent transition-colors text-foreground/80 flex items-center gap-2"
                  >
                    <service.icon className="w-4 h-4 shrink-0 text-accent/70" />
                    <span className="truncate">{service.title}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="relative group">
            <button className="hover:text-primary transition-colors flex items-center gap-1 py-4 -my-4">
              Industries <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="w-[600px] bg-background border border-primary/30 shadow-2xl p-6 grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto mt-4">
                <div className="col-span-2 mb-2 pb-2 border-b border-primary/10">
                  <p className="text-xs font-bold text-accent uppercase tracking-wider">Business Niches</p>
                </div>
                {industriesData.map(industry => (
                  <a 
                    key={industry.title} 
                    href={`/industries/${generateSlug(industry.title)}`} 
                    className="text-sm hover:text-accent transition-colors text-foreground/80 flex items-center gap-2"
                  >
                    <industry.icon className="w-4 h-4 shrink-0 text-accent/70" />
                    <span className="truncate">{industry.title}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <a href="/pricing" className="hover:text-primary transition-colors">Pricing</a>
          
          <div className="relative group">
            <button className="hover:text-primary transition-colors flex items-center gap-1 py-4 -my-4">
              Locations <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="w-[800px] bg-background border border-primary/30 shadow-2xl p-6 grid grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto mt-4">
                <div className="col-span-4 mb-2 pb-2 border-b border-primary/10">
                  <p className="text-xs font-bold text-accent uppercase tracking-wider">Top Service Areas</p>
                </div>
                {cities.map(city => (
                  <a 
                    key={city} 
                    href={`/locations/${city.toLowerCase().replace(/ /g, '-')}`} 
                    className="text-sm hover:text-accent transition-colors text-foreground/80"
                  >
                    {city}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="relative group">
            <button className="hover:text-primary transition-colors flex items-center gap-1 py-4 -my-4">
              More <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="w-[200px] bg-background border border-primary/30 shadow-2xl p-4 flex flex-col gap-3 mt-4">
                <a href="/process" className="text-sm hover:text-accent transition-colors text-foreground/80">Process</a>
                <a href="/resources" className="text-sm hover:text-accent transition-colors text-foreground/80">Resources</a>
                <a href="/#results" className="text-sm hover:text-accent transition-colors text-foreground/80">Results</a>
                <a href="/#faq" className="text-sm hover:text-accent transition-colors text-foreground/80">FAQ</a>
              </div>
            </div>
          </div>

          <a href="/contact" className="hover:text-primary transition-colors">Contact</a>
        </nav>
        <Button className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => {
          if (window.location.pathname === '/') {
            document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
          } else {
            window.location.href = '/#contact';
          }
        }}>Get Free Audit</Button>
        <button aria-label="Toggle menu" className="md:hidden p-2 text-foreground" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-background border-b border-primary/10 shadow-lg py-4 px-5 flex flex-col gap-4">
          <a href="/about" className="text-sm font-medium hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>About Us</a>
          <div className="pt-2">
            <p className="text-xs font-bold text-accent uppercase tracking-wider mb-3">Services</p>
            <div className="max-h-40 overflow-y-auto grid grid-cols-1 gap-3">
              {servicesData.map(service => (
                <a 
                  key={service.title} 
                  href={`/services/${generateSlug(service.title)}`} 
                  className="text-xs hover:text-accent transition-colors text-foreground/80 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <service.icon className="w-4 h-4 shrink-0 text-accent/70" />
                  <span className="truncate">{service.title}</span>
                </a>
              ))}
            </div>
          </div>
          <div className="pt-2">
            <p className="text-xs font-bold text-accent uppercase tracking-wider mb-3">Industries</p>
            <div className="max-h-40 overflow-y-auto grid grid-cols-1 gap-3">
              {industriesData.map(industry => (
                <a 
                  key={industry.title} 
                  href={`/industries/${generateSlug(industry.title)}`} 
                  className="text-xs hover:text-accent transition-colors text-foreground/80 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <industry.icon className="w-4 h-4 shrink-0 text-accent/70" />
                  <span className="truncate">{industry.title}</span>
                </a>
              ))}
            </div>
          </div>
          <a href="/pricing" className="text-sm font-medium hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
          <div className="pt-2">
            <p className="text-xs font-bold text-accent uppercase tracking-wider mb-3">More</p>
            <div className="flex flex-col gap-3">
              <a href="/process" className="text-xs hover:text-accent transition-colors text-foreground/80" onClick={() => setMobileMenuOpen(false)}>Process</a>
              <a href="/resources" className="text-xs hover:text-accent transition-colors text-foreground/80" onClick={() => setMobileMenuOpen(false)}>Resources</a>
              <a href="/#results" className="text-xs hover:text-accent transition-colors text-foreground/80" onClick={() => setMobileMenuOpen(false)}>Results</a>
              <a href="/#faq" className="text-xs hover:text-accent transition-colors text-foreground/80" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            </div>
          </div>
          <a href="/contact" className="text-sm font-medium hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact</a>
          <div className="pt-2">
            <p className="text-xs font-bold text-accent uppercase tracking-wider mb-3">Service Areas</p>
            <div className="max-h-40 overflow-y-auto grid grid-cols-2 gap-2">
              {cities.slice(0, 10).map(city => (
                <a 
                  key={city} 
                  href={`/locations/${city.toLowerCase().replace(/ /g, '-')}`} 
                  className="text-xs hover:text-accent transition-colors text-foreground/80"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {city}
                </a>
              ))}
              <span className="text-xs text-muted-foreground italic">and more...</span>
            </div>
          </div>
          <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 mt-2" onClick={() => {
            setMobileMenuOpen(false);
            if (window.location.pathname === '/') {
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            } else {
              window.location.href = '/#contact';
            }
          }}>Get Free Audit</Button>
        </div>
      )}
    </header>
    </>
  );
}